import React, { useState, useEffect, lazy, Suspense } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { VideoData } from "@/types";
import { getProgress } from "@/utils/api";
import { useLanguageRouting } from "@/hooks/useLanguageRouting";
import { useSEO } from "@/hooks/useSEOSchema";
import { useWarmup } from "@/hooks/useWarmup";
import StructuredData from "@/components/StructuredData";
import UserHeader from "@/components/UserHeader";
import VideoHistoryPopup from "@/components/VideoHistoryPopup";
// Import non-critical CSS for animations and effects
import "@/styles/non-critical.css";
// Auth removed
import { 
  Captions, 
  HelpCircle, 
  Star, 
  Users, 
  Clock, 
  Shield, 
  Zap, 
  Globe, 
  CheckCircle, 
  TrendingUp,
  Award,
  Download,
  PlayCircle,
  Sparkles,
  X,
  FileText
} from "lucide-react";
import LanguageSelector from "@/components/LanguageSelector";
import FileUpload from "@/components/FileUpload";
import ProcessingStatus from "@/components/ProcessingStatus";
import PreviewProgress from "@/components/PreviewProgress";
import FullProcessProgress from "@/components/FullProcessProgress";
import VideoPreview from "@/components/VideoPreview";
import PaymentSection from "@/components/PaymentSection";
import DownloadSection from "@/components/DownloadSection";
// import RecaptchaLoader from "@/components/RecaptchaLoader"; // Removed to prevent immediate loading
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

// Lazy load các components nặng không cần thiết ngay lập tức
const LazyPaymentSection = lazy(() => import("@/components/PaymentSection"));
const LazyDownloadSection = lazy(() => import("@/components/DownloadSection"));
const LazyFullProcessProgress = lazy(() => import("@/components/FullProcessProgress"));

export default function Home() {
  const { t } = useTranslation();
  const { getLocalizedPath } = useLanguageRouting();
  
  // SEO optimization
  useSEO('/');
  
  // 🚀 Warm-up SRT server when page loads
  const { hasWarmedUp, retryWarmup } = useWarmup();
  
  // Load reCAPTCHA immediately when entering home page
  const [recaptchaLoaded, setRecaptchaLoaded] = useState(false);
  
  // Login removed
  const [currentVideoId, setCurrentVideoId] = useState<string | null>(null);
  const [sourceLanguage, setSourceLanguage] = useState('auto');
  const [targetLanguage, setTargetLanguage] = useState('');
  const [processingProgress, setProcessingProgress] = useState(0);
  const [paymentCompleted, setPaymentCompleted] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isProcessingStarted, setIsProcessingStarted] = useState(false);
  const [showProcessingAfterUpload, setShowProcessingAfterUpload] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  // 🚀 NEW: Translation state for subtitle updates
  const [translatedSrtUrl, setTranslatedSrtUrl] = useState<string | null>(null);
  
  // 🚀 NEW: Callback to handle subtitle updates from DownloadSection
  const handleSubtitleUpdate = (newSrtUrl: string) => {
    console.log('🌐 home.tsx - Subtitle updated from translation:', newSrtUrl);
    setTranslatedSrtUrl(newSrtUrl);
  };

  // 🚀 NEW: Error state for video processing
  // 🚀 REMOVED: processingError state no longer needed
  
  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isMobileMenuOpen && !(event.target as Element).closest('.mobile-menu-panel')) {
        setIsMobileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMobileMenuOpen]);

  // Login UI removed

  // Handle reCAPTCHA load events
  const handleRecaptchaLoad = () => {
    console.log('✅ reCAPTCHA loaded successfully on home page');
    setRecaptchaLoaded(true);
  };

  const handleRecaptchaError = (error: string) => {
    console.warn('⚠️ reCAPTCHA load issue on home page:', error);
    // Don't treat timeout as fatal error since it may still work
  };



  // Check for payment status on component mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const videoId = urlParams.get('videoId');
    
    if (videoId) {
      // Check if payment was completed for this video
      const paymentCompleted = localStorage.getItem(`payment_completed_${videoId}`);
      const paymentTimestamp = localStorage.getItem(`payment_timestamp_${videoId}`);
      
      if (paymentCompleted && paymentTimestamp) {
        // Check if payment was made within last 24 hours
        const paymentTime = parseInt(paymentTimestamp);
        const now = Date.now();
        const hoursSincePayment = (now - paymentTime) / (1000 * 60 * 60);
        
        if (hoursSincePayment < 24) {
  
          setCurrentVideoId(videoId);
          
          // Update URL to include videoId
          const newUrl = `${window.location.pathname}?videoId=${videoId}`;
          window.history.pushState({}, '', newUrl);
        } else {
          // Clear expired payment data
          localStorage.removeItem(`payment_completed_${videoId}`);
          localStorage.removeItem(`payment_timestamp_${videoId}`);
        }
      }
    }
  }, []);

  // Fetch video data
  const { data: videoData, refetch: refetchVideo } = useQuery<any>({
            queryKey: ['/api/job/status', currentVideoId],
    queryFn: async () => {
      const progressData = await getProgress(currentVideoId!);
      
      // If video is completed, verify payment with backend
      if (progressData.video?.status === 'completed') {
        const paymentCompleted = localStorage.getItem(`payment_completed_${currentVideoId}`);
        if (paymentCompleted) {
  
        }
      }
      
      // Transform ProgressResponse to VideoData
      return {
        video: progressData.video,
        subtitles: [], // Backend doesn't return subtitles in progress response
        payment: undefined,
        queueItem: undefined
      };
    },

    enabled: !!currentVideoId,
    refetchInterval: (data: any) => {
      // Stop polling when preview is ready, preview error, or full process is completed
      const previewStatus = data?.video?.preview_process?.status;
      const fullStatus = data?.video?.full_process?.status;
      // Check if preview is ready or full is completed
      if (previewStatus === 'preview_ready' || previewStatus === 'error' || fullStatus === 'completed') {
        return false; // Return false instead of null for polling
      }
      return 3000; // Poll every 3 seconds when enabled
    },
  });

  const handleUploadStart = () => {
    setIsUploading(true);
    setUploadProgress(0);
  };

  const handleUploadComplete = (videoId: string, cached: boolean, videoData?: any) => {
    
    setCurrentVideoId(videoId);
    setIsUploading(false);
    setUploadProgress(100);
    setIsProcessingStarted(true);
    setShowProcessingAfterUpload(true);
    
    // Show preview immediately after upload response 200
    
    
    // Reset payment status for new video
    setPaymentCompleted(false);
    
    // Refetch immediately to get initial processing status
    setTimeout(() => {
      refetchVideo();
    }, 100);
  };

  const handleUploadProgress = (progress: number) => {
    setUploadProgress(progress);
  };

  const handlePaymentComplete = () => {
    // Save payment status to localStorage to persist across page reloads
    if (currentVideoId) {
      localStorage.setItem(`payment_completed_${currentVideoId}`, 'true');
      localStorage.setItem(`payment_timestamp_${currentVideoId}`, Date.now().toString());
      
      // Update URL to include videoId for persistence
      const newUrl = `${window.location.pathname}?videoId=${currentVideoId}`;
      window.history.pushState({}, '', newUrl);
    }
    
    // Update local state to show download section without reloading
    setPaymentCompleted(true);
    
    // Refetch to get updated video status
    refetchVideo();
  };

  const video = videoData?.video;
  const subtitles = videoData?.subtitles || [];
  const previewSubtitle = subtitles.find((s: any) => s.is_preview);
  const fullSubtitle = subtitles.find((s: any) => !s.is_preview);

  // 🚀 REMOVED: Error handling moved to useVideoStatus with direct DOM manipulation

  // Reset processing fallback only when backend reports a concrete processing state
  useEffect(() => {
    const vpStatus = videoData?.video?.preview_process?.status;
    const fpStatus = videoData?.video?.full_process?.status;
    if (vpStatus || fpStatus) {
      setShowProcessingAfterUpload(false);
      setIsProcessingStarted(false);
    }
  }, [videoData]);

  // Trust indicators data
  const stats = [
    { icon: Users, value: "50,000+", label: "Videos Processed" },
    { icon: Star, value: "4.9/5", label: "User Rating" },
    { icon: Globe, value: "40+", label: "Languages" },
    { icon: Clock, value: "99.5%", label: "Uptime" }
  ];

  const features = [
    {
      icon: Zap,
      title: "Video to SRT Converter",
      description: "Convert video to subtitle format in minutes with AI transcription"
    },
    {
      icon: Shield,
      title: "Secure Subtitle Generation",
      description: "Files automatically deleted after 1 hour for privacy"
    },
    {
      icon: Globe,
      title: "Multi-Language Subtitle Generator",
      description: "Support for 40+ languages with auto-detection and translation"
    },
    {
      icon: Award,
      title: "Professional Subtitle Quality",
      description: "Generate accurate SRT subtitles with precise timestamps"
    }
  ];

  const testimonials = [
    {
      name: "Sarah Chen",
      role: "Content Creator",
      rating: 5,
      comment: "This video to subtitle generator saved me hours of manual work. The SRT accuracy is incredible!"
    },
    {
      name: "Marcus Weber",
      role: "Film Producer",
      rating: 5,
      comment: "Best video subtitle converter I've used. Professional quality SRT files at a fraction of the cost."
    },
    {
      name: "Elena Rodriguez",
      role: "Educator",
      rating: 5,
      comment: "Perfect video to subtitle tool for making educational content accessible worldwide with SRT format."
    }
  ];

  return (
    <>
      <StructuredData type="organization" />
      <StructuredData type="website" />
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      {/* reCAPTCHA will be loaded on demand when needed */}
      
      {/* Header */}
      <header className="glass-card shadow-sm border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <h1 
                  className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent cursor-pointer hover:opacity-80 transition-opacity"
                  onClick={() => window.location.href = '/'}
                  title="Click to go home"
                >
                  <Captions className="inline h-6 w-6 mr-2 text-blue-600" />
                  {t('brand')}
                </h1>
              </div>
              <div className="hidden md:block ml-10">
                <Badge variant="secondary" className="text-xs font-medium">
                  <Sparkles className="h-3 w-3 mr-1" />
                  AI-Powered
                </Badge>
              </div>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-4">
              <LanguageSelector />
              <Button variant="ghost" size="sm" onClick={() => window.location.href = '/features'}>
                Features
              </Button>
              <Button variant="ghost" size="sm" onClick={() => window.location.href = '/pricing'}>
                Pricing
              </Button>
              <Button variant="ghost" size="sm" onClick={() => window.location.href = getLocalizedPath('/about')}>
                About
              </Button>
              <Button variant="ghost" size="sm" onClick={() => window.location.href = getLocalizedPath('/support')}>
                Support
              </Button>
              <VideoHistoryPopup />
              <Button variant="ghost" size="sm" aria-label="Help and support">
                <HelpCircle className="h-5 w-5" />
              </Button>
              <UserHeader />
            </div>

            {/* Mobile Menu Button */}
            <div className="md:hidden">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="p-2"
                aria-label="Toggle mobile menu"
              >
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Menu Panel */}
      {isMobileMenuOpen && (
        <div className="md:hidden fixed inset-0 z-50 bg-black bg-opacity-50">
          <div className={`mobile-menu-panel fixed inset-y-0 right-0 w-80 bg-white shadow-xl transform transition-transform duration-300 ease-in-out ${
            isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
          }`}>
            {/* Mobile Menu Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Menu</h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsMobileMenuOpen(false)}
                className="p-2"
              >
                <X className="h-5 w-5" />
              </Button>
            </div>

            {/* Mobile Menu Content */}
            <div className="p-4 space-y-4">
              {/* Language Selector */}
              <div className="border-b border-gray-200 pb-4">
                <LanguageSelector />
              </div>

              {/* Navigation Links */}
              <nav className="space-y-2">
                <Button
                  variant="ghost"
                  size="lg"
                  onClick={() => {
                    window.location.href = '/features';
                    setIsMobileMenuOpen(false);
                  }}
                  className="w-full justify-start text-left h-12 px-4"
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Sparkles className="h-4 w-4 text-blue-600" />
                    </div>
                    <span>Features</span>
                  </div>
                </Button>

                <Button
                  variant="ghost"
                  size="lg"
                  onClick={() => {
                    window.location.href = '/pricing';
                    setIsMobileMenuOpen(false);
                  }}
                  className="w-full justify-start text-left h-12 px-4"
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                      <Star className="h-4 w-4 text-green-600" />
                    </div>
                    <span>Pricing</span>
                  </div>
                </Button>

                <Button
                  variant="ghost"
                  size="lg"
                  onClick={() => {
                    window.location.href = getLocalizedPath('/about');
                    setIsMobileMenuOpen(false);
                  }}
                  className="w-full justify-start text-left h-12 px-4"
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                      <Users className="h-4 w-4 text-purple-600" />
                    </div>
                    <span>About</span>
                  </div>
                </Button>

                <Button
                  variant="ghost"
                  size="lg"
                  onClick={() => {
                    window.location.href = getLocalizedPath('/support');
                    setIsMobileMenuOpen(false);
                  }}
                  className="w-full justify-start text-left h-12 px-4"
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
                      <HelpCircle className="h-4 w-4 text-orange-600" />
                    </div>
                    <span>Support</span>
                  </div>
                </Button>

                <div className="w-full px-4">
                  <VideoHistoryPopup />
                </div>
              </nav>

              {/* User Authentication */}
              <div className="border-t border-gray-200 pt-4">
                <UserHeader />
              </div>

              {/* Divider */}
              <div className="border-t border-gray-200 pt-4">
                <div className="text-xs text-gray-500 text-center">
                  AI-Powered Subtitle Generation
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Hero Section */}
      {!currentVideoId && (
        <section className="relative py-20 px-4 sm:px-6 lg:px-8 slide-in-up">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16 float-animation">
              <h1 className="text-5xl font-bold text-gray-900 mb-6 slide-in-up">
                <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  {t('home.hero.title')}
                </span>
                <span className="block text-gray-900">
                  {t('home.hero.subtitle')}
                </span>
              </h1>
              <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
                {t('home.hero.description')}
              </p>
              
              {/* Trust Indicators */}
              <div className="flex flex-wrap justify-center gap-6 mb-12 stat-item">
                {stats.map((stat, index) => (
                  <div key={index} className="flex items-center space-x-2 text-gray-700">
                    <stat.icon className="h-5 w-5 text-blue-600" />
                    <span className="font-semibold">{stat.value}</span>
                    <span className="text-sm text-gray-500">{stat.label}</span>
                  </div>
                ))}
              </div>

            </div>

            {/* 🚀 REMOVED: Error message now handled by useVideoStatus with direct DOM manipulation */}

            <FileUpload />

            {/* Features Grid */}
            <div className="mt-20">
              <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
                {t('home.features.title')}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                {features.map((feature, index) => (
                  <Card key={index} className="border-0 shadow-lg feature-card bg-white/50 backdrop-blur-sm">
                    <CardContent className="p-6 text-center">
                      <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg flex items-center justify-center mx-auto mb-4">
                        <feature.icon className="h-6 w-6 text-white" />
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">{feature.title}</h3>
                      <p className="text-gray-600 text-sm">{feature.description}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {/* Pricing Preview */}
            <div className="mt-20">
              <div className="animated-gradient rounded-2xl p-8 text-white text-center pricing-card">
                <h2 className="text-2xl font-bold mb-4">{t('home.pricing.title')}</h2>
                <div className="text-4xl font-bold mb-2">{t('home.pricing.price')} <span className="text-lg opacity-80">{t('home.pricing.perMinute')}</span></div>
                <p className="opacity-90 mb-6">{t('home.pricing.description')}</p>
                <div className="flex flex-wrap justify-center gap-4 text-sm">
                  <div className="flex items-center">
                    <CheckCircle className="h-4 w-4 mr-2" />
                    {t('home.pricing.freePreview')}
                  </div>
                  <div className="flex items-center">
                    <CheckCircle className="h-4 w-4 mr-2" />
                    {t('home.pricing.multipleFormats')}
                  </div>
                  <div className="flex items-center">
                    <CheckCircle className="h-4 w-4 mr-2" />
                    {t('home.pricing.autoTranslation')}
                  </div>
                </div>
              </div>
            </div>

            {/* Testimonials */}
            <div className="mt-20">
              <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
                {t('home.testimonials.title')}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {testimonials.map((testimonial, index) => (
                  <Card key={index} className="border-0 shadow-lg bg-white testimonial-card">
                    <CardContent className="p-6">
                      <div className="flex items-center mb-4">
                        {[...Array(testimonial.rating)].map((_, i) => (
                          <Star key={i} className="h-4 w-4 text-yellow-400 fill-current" />
                        ))}
                      </div>
                      <p className="text-gray-700 mb-4 italic">"{testimonial.comment}"</p>
                      <div>
                        <div className="font-semibold text-gray-900">{testimonial.name}</div>
                        <div className="text-sm text-gray-500">{testimonial.role}</div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {/* Technology Section */}
            <div className="mt-20">
              <div className="text-center">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">
                  {t('home.technology.title')}
                </h2>
                <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
                  {t('home.technology.description')}
                </p>
                <div className="flex flex-wrap justify-center gap-8 items-center opacity-60">
                  <div className="text-lg font-semibold">OpenAI Whisper AI</div>
                  <div className="text-lg font-semibold">Google Translate</div>
                  <div className="text-lg font-semibold">Secure Processing</div>
                  <div className="text-lg font-semibold">FFmpeg Video</div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

                {/* Processing sections - show when uploading, processing started, or video is uploaded */}
          {(isUploading || isProcessingStarted || currentVideoId) && (
            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
              {/* Upload Loading State */}
              {isUploading && (
                <div className="mb-8">
                  <div className="glass-card rounded-xl shadow-lg border border-white/20 p-8">
                    <div className="text-center">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">Uploading Video...</h3>
                      <p className="text-gray-600">Please wait while we upload your video</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Processing Started State - Show immediately when upload starts */}
              {(isUploading || showProcessingAfterUpload) && !video && (
                <div className="mb-8">
                  <ProcessingStatus
                    videoId={currentVideoId || ''}
                    status="processing"
                    progress={0}
                    sourceLanguage={sourceLanguage}
                    targetLanguage={targetLanguage}
                    onSourceLanguageChange={setSourceLanguage}
                    onTargetLanguageChange={setTargetLanguage}
                    onStatusChange={(status, progress) => {
                      setProcessingProgress(progress);
                    }}
                  />
                </div>
              )}

              {/* Combined Upload and Preview Processing Status */}
              {(() => {
                const isPreviewActive = video && (video.preview_process?.status === 'processing' || video.preview_process?.status === 'uploading');
                const shouldShow = isUploading || showProcessingAfterUpload || (isPreviewActive && video.full_process?.status !== 'full_processing');

                return shouldShow;
              })() && (
                <div className="mb-8">
                  <ProcessingStatus
                    videoId={video?.id || currentVideoId || ''}
                    status={video?.preview_process?.status || video?.status || (isUploading ? 'uploading' : 'processing')}
                    progress={video?.preview_process?.progress ?? processingProgress}
                    isUploading={isUploading}
                    uploadProgress={uploadProgress}
                    sourceLanguage={sourceLanguage}
                    targetLanguage={targetLanguage}
                    onSourceLanguageChange={setSourceLanguage}
                    onTargetLanguageChange={setTargetLanguage}
                    onStatusChange={(status, progress) => {
                      setProcessingProgress(progress);
                      // Update video status if needed
                      if (video?.preview_process?.status !== status) {
                        refetchVideo();
                      }
                    }}
                    onVideoStatusUpdate={(videoStatus) => {
                      // 🚀 NEW: Update local state with video status from ProcessingStatus
                      console.log('🎬 home.tsx - Received video status update:', videoStatus);
                      // Force re-render by updating state
                      setProcessingProgress(videoStatus.progress || 0);
                    }}
                  />
                </div>
              )}

          {/* Video Preview - Show when preview completed */}
          {(() => {
            const shouldShowPreview = video && (video.preview?.status === 'completed' || video.preview_process?.status === 'preview_ready' || video.preview_process?.status === 'completed');
            console.log('🎬 home.tsx - VideoPreview render check:', {
              video: video,
              videoPreviewStatus: video?.preview?.status,
              videoPreviewProcessStatus: video?.preview_process?.status,
              shouldShowPreview: shouldShowPreview,
              videoId: video?.id
            });
            return shouldShowPreview;
          })() && (
            <div className="mb-8">
              <VideoPreview
                videoId={video.id}
                jobId={video.preview?.job_id || video.preview_process?.job_id || 'preview'}
              />
            </div>
          )}

          {/* Preview Error - Stop everything and show error state */}
          {video && video.preview_process?.status === 'error' && (
            <div className="mb-8" data-progress-section>
              <PreviewProgress
                videoId={video.id}
                status={video.preview_process?.status}
                progress={video.preview_process?.progress || 0}
              />
            </div>
          )}

          {/* Full Process Progress - Only show when full process is active */}
          {video && video.full_process?.status === 'full_processing' && (
            <div className="mb-8" data-progress-section>
              <FullProcessProgress
                videoId={video.id}
                status={video.full_process?.status}
                progress={video.full_process?.progress || 0}
                subtitleDone={video.full_process?.subtitle_done || false}
                videoDone={video.full_process?.video_done || false}
              />
            </div>
          )}

          {/* Payment Section - Show when preview completed and no payment completed */}
          {video && (video.preview?.status === 'completed' || video.preview_process?.status === 'preview_ready' || video.preview_process?.status === 'completed') && !paymentCompleted && (
            <div className="mb-8" data-payment-section>
              <PaymentSection
                videoId={video.id}
                jobId={video.preview?.job_id || video.preview_process?.job_id} // 🚀 Pass jobId for useVideoStatus hook
                onPaymentComplete={handlePaymentComplete}
                onShowLoginModal={() => { /* no-op, login removed */ }}
              />
            </div>
          )}

          {/* Download Section - Show only when full process completed */}
          {video && video.full_process?.status === 'completed' && (
            <div className="mb-8">
              <DownloadSection
                videoId={video.id}
                originalFilename={video.original_name}
                onSubtitleUpdate={handleSubtitleUpdate} // Pass the callback
              />
            </div>
          )}

      
        </main>
      )}

      {/* Login & History removed */}

      {/* Footer */}
      <footer className="bg-gray-900 text-white mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <h3 className="font-semibold text-lg mb-4">
                <Captions className="inline h-5 w-5 mr-2" />
                {t('brand')}
              </h3>
              <p className="text-gray-400 text-sm mb-4">{t('tagline')}</p>
              <div className="flex items-center text-sm text-gray-400">
                <Shield className="h-4 w-4 mr-2" />
                Enterprise-grade security
              </div>
            </div>
            <div>
              <h4 className="font-medium text-white mb-4">{t('footer.features')}</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li className="flex items-center"><CheckCircle className="h-3 w-3 mr-2 text-green-400" />{t('footer.speechRecognition')}</li>
                <li className="flex items-center"><CheckCircle className="h-3 w-3 mr-2 text-green-400" />{t('footer.multiLanguage')}</li>
                <li className="flex items-center"><CheckCircle className="h-3 w-3 mr-2 text-green-400" />{t('footer.autoTranslation')}</li>
                <li className="flex items-center"><CheckCircle className="h-3 w-3 mr-2 text-green-400" />{t('footer.multipleFormats')}</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium text-white mb-4">{t('footer.support')}</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><a href="/support" className="hover:text-white transition-colors cursor-pointer">{t('footer.helpCenter')}</a></li>
                <li><a href="/features" className="hover:text-white transition-colors cursor-pointer">{t('footer.apiDocs')}</a></li>
                <li><a href="/support" className="hover:text-white transition-colors cursor-pointer">{t('footer.contact')}</a></li>
                <li><a href="/support" className="hover:text-white transition-colors cursor-pointer">{t('footer.status')}</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium text-white mb-4">{t('footer.legal')}</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><a href="/privacy" className="hover:text-white transition-colors cursor-pointer">Privacy Policy</a></li>
                <li><a href="/terms" className="hover:text-white transition-colors cursor-pointer">Terms of Service</a></li>
                <li><a href="/refund" className="hover:text-white transition-colors cursor-pointer">Refund Policy</a></li>
                <li><a href="/about" className="hover:text-white transition-colors cursor-pointer">About Us</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 flex flex-col md:flex-row justify-between items-center">
            <div className="text-sm text-gray-400 mb-4 md:mb-0">
              <p>© 2025 DONG HOA AI DATA GENERATION LTD — Registered in Northern Ireland (Company no. NI733203)</p>
              <p className="mt-1">Office 1334, 92 Castle Street, Belfast BT1 1HE, United Kingdom</p>
            </div>
            <div className="flex items-center space-x-4 text-sm text-gray-400">
              <div className="flex items-center">
                <TrendingUp className="h-4 w-4 mr-2 text-green-400" />
                99.5% Uptime
              </div>
              <div className="flex items-center">
                <Users className="h-4 w-4 mr-2 text-blue-400" />
                50,000+ Videos
              </div>
            </div>
          </div>
        </div>
      </footer>
      </div>
    </>
  );
}